import { MenuItem } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import {
  CONTRACT_DATE_FORMAT_OPTIONS,
  DEFAULT_CONTRACT_DATE_FORMAT,
} from 'src/constants/dateConsts';
import { FormBodyAPI } from 'src/constants/fieldsTypes';
import { SuggestIconPicker } from 'src/features/SuggestIconPicker';
import { commonValidationSchemaItems } from 'src/hooks/useEditModule';
import { AppVisibilityConfigSetting } from 'src/legacy/components/app-visibility/AppVisibilityConfigSetting';
import { RemoveApp } from 'src/legacy/components/AppSetup/RemoveApp';
import RowDivider from 'src/legacy/components/RowDivider';
import { SettingsPageRow } from 'src/legacy/components/Settings';
import SettingsToggleCard from 'src/legacy/components/Settings/SettingsToggleCard';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';
import { GraySmall } from 'src/theme/colors';
import { VisibilityScenario } from 'src/utils/app_visibility';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  helperText: {
    color: GraySmall,
  },
  iconPickerWrapper: {
    marginTop: theme.spacing(3.5),
    marginBottom: theme.spacing(3.5),
  },
  removeApp: {
    paddingTop: theme.spacing(5),
  },
  dateFormatField: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  dateFormatLabel: {
    marginBottom: theme.spacing(1),
  },
}));

export const useContractSettingsForm = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const validationScheme = Yup.object().shape({
    ...commonValidationSchemaItems,
    isEmailAttachmentExcluded: Yup.boolean(),
    dateFormat: Yup.string().oneOf([
      ...CONTRACT_DATE_FORMAT_OPTIONS.map((o) => o.value),
      '',
    ]),
  });

  const FormRenderer: React.FC<FormBodyAPI> = ({
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    setFieldValue,
  }) => (
    <>
      <BaseTextField
        autoFocus
        fullWidth
        label={
          <div className={classes.container}>
            <BaseTypography fontType="13Medium">Name</BaseTypography>
            <BaseTypography className={classes.helperText}>
              &nbsp;- How it should appear in the sidebar
            </BaseTypography>
          </div>
        }
        variant="outlined"
        sizeVariant="medium"
        name="label"
        value={values.label}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched && touched.label && errors && errors.label)}
        helperText={touched && touched.label && errors && errors.label}
      />
      <div className={classes.iconPickerWrapper}>
        <BaseTypography fontType="13Medium">Sidebar icon</BaseTypography>
        <SuggestIconPicker
          value={values.icon}
          onChange={(iconName: string) => setFieldValue('icon', iconName)}
          appName={values.label}
        />
      </div>

      <SettingsPageRow hideDivider>
        <BaseTypography fontType="18Medium">Preferences</BaseTypography>
        <BaseTypography className={classes.helperText}>
          Personalize the Contracts App
        </BaseTypography>
      </SettingsPageRow>
      <RowDivider mb={0} />
      <SettingsToggleCard
        title="Email notifications contain pdf attachments"
        description={t(($) => $.page_contracts.email_attachment_description)}
        fieldName="isEmailAttachmentExcluded"
        invertBooleanValue
        checked={values.isEmailAttachmentExcluded}
      />
      <div className={classes.dateFormatField}>
        <BaseTypography fontType="13Medium" className={classes.dateFormatLabel}>
          Date format
        </BaseTypography>
        <BaseTypography className={classes.helperText}>
          The format used for dates on signed contracts and date inputs.
        </BaseTypography>
        <BaseTextField
          fullWidth
          select
          variant="outlined"
          sizeVariant="medium"
          name="dateFormat"
          value={values.dateFormat || DEFAULT_CONTRACT_DATE_FORMAT}
          onChange={(event) => setFieldValue('dateFormat', event.target.value)}
          onBlur={handleBlur}
          error={Boolean(touched?.dateFormat && errors?.dateFormat)}
          helperText={touched?.dateFormat && errors?.dateFormat}
        >
          {CONTRACT_DATE_FORMAT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </BaseTextField>
      </div>
      <AppVisibilityConfigSetting scenario={VisibilityScenario.EditApp} />
      <div className={classes.removeApp}>
        <RemoveApp />
      </div>
    </>
  );

  return {
    validationScheme,
    FormRenderer,
  };
};
